import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import RootState from '../redux/states/root.state';
import { fetchV1MqttToken } from '../services/api/v1Mqtt.api';
import V1SpaClient, { V1TokenInfo } from '../services/mqtt/v1/v1Client';
import { V1Channel } from '../services/mqtt/v1/v1Transforms';

/**
 * Firmware progress a V1 / Coldfire spa publishes on `arctic/spa/{guid}/telemetry/update`
 * while an update runs: `progress` is 0-100, or -1 while it is starting.
 */
export interface V1UpdateProgress {
    done: boolean;
    progress: number;
    queue?: number;
    success?: boolean;
}

/**
 * Listens to one V1 spa's firmware progress on the legacy broker, for the details page,
 * which otherwise reads the legacy database and keeps no broker socket of its own. Pass an
 * empty `spaId` to stay idle. Only the `update` channel is used; the rest is ignored.
 */
const useV1UpdateProgress = (spaId: string): V1UpdateProgress | null => {
    const accessToken = useSelector((state: RootState) => state.user.accessToken);
    const accessTokenRef = useRef<string | null>(accessToken);
    useEffect(() => {
        accessTokenRef.current = accessToken;
    }, [accessToken]);

    const [progress, setProgress] = useState<V1UpdateProgress | null>(null);

    useEffect(() => {
        setProgress(null);
        if (!spaId) return;

        let disposed = false;
        const client = new V1SpaClient(spaId, {
            sourceName: 'Web',
            getToken: async (): Promise<V1TokenInfo> => {
                const res = await fetchV1MqttToken(accessTokenRef.current, spaId);
                return {
                    token: res.access_token,
                    expiresAt: Date.now() + (Number(res.expires_in) || 3600) * 1000,
                    brokerUrl: res.broker_url ?? '',
                    spaGuid: res.spa_guid,
                };
            },
            onMessage: (channel: V1Channel, payload: any) => {
                if (disposed || channel !== 'update' || !payload || typeof payload !== 'object') return;
                setProgress({
                    done: Boolean(payload.done),
                    progress: Number(payload.progress ?? 0),
                    queue: payload.queue,
                    success: payload.success,
                });
            },
        });
        client.start();

        return () => {
            disposed = true;
            client.stop();
        };
    }, [spaId]);

    return progress;
};

export default useV1UpdateProgress;
